import * as React from 'react';
import { referenceData } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { ErrorBoundary } from '../../layouts/error-boundary';

export interface FormatEnumOptionsResult {
  /** Distinct media-format `code` values (not label or id) — the import pipeline resolves
   * a mapped/constant 'format' value against media_formats.code, case-insensitively (see
   * media-import.job.ts resolveSupplierAndFormat). */
  options: readonly string[];
  isLoading: boolean;
  isError: boolean;
}

interface FormatEnumOptionsProps {
  children: (result: FormatEnumOptionsResult) => React.ReactNode;
}

function LoadedFormatEnumOptions({ children }: FormatEnumOptionsProps) {
  const { data } = useApiSuspenseQuery(referenceData.formats, {});
  return children({
    options: data.data.map((format) => format.code),
    isLoading: false,
    isError: false,
  });
}

const LOADING: FormatEnumOptionsResult = { options: [], isLoading: true, isError: false };
const FAILED: FormatEnumOptionsResult = { options: [], isLoading: false, isError: true };

/**
 * Resolves the 'format' FIELD_OPTIONS key's closed value set from the same per-tenant
 * GET /media-formats dictionary the create/edit media-unit forms already query — 'format' has
 * no static @ooh/contracts enum export the way type/material/lighting do (see
 * constant-value-enum-options.ts). The editors keep their own loading / error banners, so
 * this renders them in those states instead of blocking the dialog.
 */
export function FormatEnumOptions({ children }: FormatEnumOptionsProps) {
  return (
    <ErrorBoundary onReset={() => undefined} renderError={() => children(FAILED)}>
      <React.Suspense fallback={children(LOADING)}>
        <LoadedFormatEnumOptions>{children}</LoadedFormatEnumOptions>
      </React.Suspense>
    </ErrorBoundary>
  );
}
